'use client';

import { Suspense, useMemo, useRef, useState } from 'react';

import dynamic from 'next/dynamic';

import R2Image from '@/components/ui/R2Image';
import {
  doctorEnquiryDefaultValues,
  doctorEnquiryFields,
  doctorEnquirySchema,
} from '@/features/doctors/forms/doctorEnquirySchema';
import SpecialistBiography from '@/features/specialists/components/SpecialistBiography/SpecialistBiography';
import { getDoctorAllSpecialities } from '@/features/specialists/utils/specialistFiltering';
import { pushGtmEvent } from '@/lib/analytics/gtm';
import { GTM_EVENTS } from '@/lib/analytics/gtmEvents';
import type { Doctor, Location as DoctorLocation, Tab } from '@/types/doctor';

const LIPS_APPOINTMENTS_EMAIL = 'appointments@lips.org.uk';
const LIPS_MAIN_PHONE = '+44 20 7164 6114';
const EMAIL_LINE_PATTERN = /\b[^\s@]+@[^\s@]+\.[^\s@]+\b/i;
const PHONE_LINE_PATTERN = /^[+\d\s().-]+$/;
const PHONE_PATTERN = /\+?\d[\d\s().-]{7,}\d/g;
const WEBSITE_PATTERN = /https?:\/\/[^\s]+/i;
const TEDDY_BEAR_SPECIALITIES = new Set([
  'paediatrics',
  'general-practitioner',
]);

export const shouldShowTeddyBear = (
  doctor: Pick<Doctor, 'primarySpeciality' | 'specialities' | 'canSeeChildren'>,
): boolean =>
  doctor.canSeeChildren === true ||
  getDoctorAllSpecialities(doctor).some((speciality) =>
    TEDDY_BEAR_SPECIALITIES.has(speciality.slug),
  );

const HOSPITAL_WEBSITES: Array<[RegExp, string]> = [
  [/cromwell hospital/i, 'https://www.cromwellhospital.com/'],
  [/chelsea and westminster/i, 'https://www.chelwest.nhs.uk/'],
  [/imperial college healthcare/i, 'https://www.imperial.nhs.uk/'],
  [
    /st\.?\s*george(?:'|’)?s hospital/i,
    'https://www.stgeorges.nhs.uk/st-georges-hospital/',
  ],
  [
    /university college london hospital/i,
    'https://www.uclh.nhs.uk/our-services/our-hospitals/university-college-hospital',
  ],
  [/king(?:'|’)?s college hospital/i, 'https://www.kch.nhs.uk/'],
  [
    /king edward vii(?:'|’)?s?(?: hospital)?/i,
    'https://www.kingedwardvii.co.uk/',
  ],
  [/elite sports professional services/i, 'https://esps.london/'],
  [/addenbrooke|cambridge university hospitals/i, 'https://www.cuh.nhs.uk/'],
  [
    /barking havering redbridge|queens hospital/i,
    'https://www.bhrhospitals.nhs.uk/queens-hospital',
  ],
  [/barts health/i, 'https://www.bartshealth.nhs.uk/'],
  [
    /the royal london hospital/i,
    'https://www.bartshealth.nhs.uk/the-royal-london',
  ],
  [/east sussex healthcare/i, 'https://www.esht.nhs.uk/'],
  [
    /guy(?:'|’)?s and st\.? thomas|st thomas(?:'|’)? hospital/i,
    'https://www.guysandstthomas.nhs.uk/',
  ],
  [/homerton hospital/i, 'https://www.homerton.nhs.uk/'],
  [/kingston private healthcare/i, 'https://kingstonprivatehealth.co.uk/'],
  [/london north west university hospitals/i, 'https://www.lnwh.nhs.uk/'],
  [/medway nhs/i, 'https://www.medway.nhs.uk/'],
  [
    /musgrove park hospital/i,
    'https://www.somersetft.nhs.uk/musgrove-park-hospital/',
  ],
  [
    /royal national ear.*eastman dental/i,
    'https://www.uclh.nhs.uk/our-services/our-hospitals/royal-national-ent-and-eastman-dental-hospitals',
  ],
  [/royal berkshire hospital/i, 'https://www.royalberkshire.nhs.uk/'],
  [
    /royal free london hospital/i,
    'https://www.royalfree.nhs.uk/our-locations/royal-free-hospital',
  ],
  [/royal marsden hospital/i, 'https://www.royalmarsden.nhs.uk/'],
  [/royal surrey hospital/i, 'https://www.royalsurrey.nhs.uk/'],
  [
    /mount alvernia hospital/i,
    'https://www.circlehealthgroup.co.uk/hospitals/mount-alvernia-hospital',
  ],
  [/ume health/i, 'https://umehealth.co.uk/'],
  [/cleveland clinic/i, 'https://clevelandcliniclondon.uk/'],
  [/one\s?welbeck/i, 'https://onewelbeck.com/'],
  [/the london clinic/i, 'https://www.thelondonclinic.co.uk/'],
  [/fortius clinic london/i, 'https://www.fortiusclinic.com/en/'],
  [
    /fortius clinic city/i,
    'https://www.fortiusclinic.com/en/clinics/fortius-clinic-city/',
  ],
  [/fortius clinic/i, 'https://www.fortiusclinic.com/'],
  [/cadogan clinic/i, 'https://www.cadoganclinic.com/'],
  [/lycahealth/i, 'https://lycahealth.com/'],
  [/phoenix hospital/i, 'https://www.phoenixhospitalgroup.com/'],
  [/priory/i, 'https://www.priorygroup.com/'],
  [/spire\b/i, 'https://www.spirehealthcare.com/'],
  [
    /nuffi(?:e|)ld|parkside hospital/i,
    'https://www.nuffieldhealth.com/hospitals',
  ],
  [
    /\bcircle\b|blackheath|cavell|chelsfield|clementine churchill|hendon hospital|highgate private|london independent|north downs|sarum road|shelburne|shirley oaks|sloane hospital/i,
    'https://www.circlehealthgroup.co.uk/',
  ],
  [
    /hca\b|chelsea outpatient|devonshire diagnostic|harley street clinic|lister hospital|london bridge hospital|platinum medical|portland (?:hospital|place outpatient)|princess grace|the shard|wellington hospital|private care at guy|canary wharf outpatients/i,
    'https://www.hcahealthcare.co.uk/locations/hospitals',
  ],
];

const isBatterseaLocation = (heading: string) =>
  /battersea power station/i.test(heading);

const isPhoneLine = (line: string) =>
  PHONE_LINE_PATTERN.test(line) && line.replace(/\D/g, '').length >= 7;

const appendUniqueLine = (lines: string[], value: string) => {
  const normalizedValue = value.trim().toLowerCase();
  if (
    normalizedValue &&
    !lines.some((line) => line.trim().toLowerCase() === normalizedValue)
  ) {
    lines.push(value.trim());
  }
};

const getHospitalWebsite = (heading: string) =>
  HOSPITAL_WEBSITES.find(([pattern]) => pattern.test(heading))?.[1];

export const normalizeConsultantLocation = (
  location: DoctorLocation,
  doctor: Doctor,
): DoctorLocation => {
  const lines = location.description
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  if (!isBatterseaLocation(location.heading)) {
    const existingWebsite = location.description.match(WEBSITE_PATTERN)?.[0];
    const website = existingWebsite || getHospitalWebsite(location.heading);
    const sanitizedLines = lines
      .map((line) =>
        line
          .replace(EMAIL_LINE_PATTERN, '')
          .replace(PHONE_PATTERN, '')
          .replace(WEBSITE_PATTERN, '')
          .trim(),
      )
      .filter((line) => line && !isPhoneLine(line));

    if (website) appendUniqueLine(sanitizedLines, website);

    return {
      ...location,
      description: sanitizedLines.join('\n'),
    };
  }

  const contactEmail = LIPS_APPOINTMENTS_EMAIL;
  const contactPhone = doctor.bookable
    ? doctor.assistantPhoneNumber?.trim() || LIPS_MAIN_PHONE
    : LIPS_MAIN_PHONE;

  const batterseaLines = lines.filter(
    (line) =>
      !WEBSITE_PATTERN.test(line) &&
      !EMAIL_LINE_PATTERN.test(line) &&
      !isPhoneLine(line),
  );

  appendUniqueLine(batterseaLines, contactEmail);
  appendUniqueLine(batterseaLines, contactPhone);

  return { ...location, description: batterseaLines.join('\n') };
};

const BookingCard = dynamic(
  () => import('@/features/specialists/components/BookingCard/BookingCard'),
  {
    ssr: false,
    loading: () => (
      <div
        className="h-[480px] w-full animate-pulse rounded-xl bg-gray-50"
        aria-hidden="true"
      />
    ),
  },
);

const SpecialistTabs = dynamic(
  () =>
    import('@/features/specialists/components/SpecialistTabs/SpecialistTabs'),
  {
    ssr: false,
    loading: () => (
      <div
        className="h-[200px] w-full animate-pulse rounded-xl bg-gray-50"
        aria-hidden="true"
      />
    ),
  },
);

const FormSubmissionModal = dynamic(
  () =>
    import('@/components/forms/FormSubmissionModal/FormSubmissionModal').then(
      (m) => ({ default: m.FormSubmissionModal }),
    ),
  { ssr: false },
);

const ConsultantDoctifyRatingCarousel = dynamic(
  () =>
    import('@/components/widgets/DoctifyRatingCarousel/DoctifyRatingCarousel').then(
      (m) => ({ default: m.ConsultantDoctifyRatingCarousel }),
    ),
  {
    ssr: false,
    loading: () => (
      <div
        className="h-[160px] w-full animate-pulse rounded-xl bg-gray-50"
        aria-hidden="true"
      />
    ),
  },
);

const TEXT = {
  about: 'About',
  gmcLabel: 'GMC: ',
  hcpcLabel: 'HCPC: ',
  contactTitle: 'Contact us',
  messageButton: 'Message',
  callButton: 'Call Us',
  languagesTitle: 'Languages',
};

export default function SpecialistDetails({ data }: { data: any }) {
  const isDoctor = !data.slug?.toLowerCase().includes('clinic');
  const displayName = data.fullName || data.title;
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const doctifyRef = useRef<HTMLDivElement>(null);

  const hasDoctify = isDoctor && Boolean(data.doctifySlug);

  const stats = useMemo(() => {
    const list: Array<{
      label: string;
      sub: string;
      image: string;
      doctify?: boolean;
    }> = [];
    if (hasDoctify) {
      list.push({
        label: 'Doctify',
        sub: 'Reviews',
        image: '/images/specialists/doctify-apple.png',
        doctify: true,
      });
    }
    list.push({
      label: '100+',
      sub: 'Reviews',
      image: '/images/specialists/slug/message.svg',
    });
    return list;
  }, [hasDoctify]);

  const allTabs: Tab[] = data.tabSection || [];
  const bioTab = allTabs.find(
    (t) =>
      t.heading?.toLowerCase().includes('biography') ||
      t.heading?.toLowerCase().includes('about'),
  );
  const nonBioTabs = allTabs.filter((t) => t !== bioTab);

  const biography = (() => {
    const content = bioTab?.description || data.description || '';
    if (typeof content === 'string') return content;
    if (Array.isArray(content))
      return content.map((l) => l.description).join('\n');
    return '';
  })();

  const pillsTabs = useMemo(() => {
    const primaryHeadings = new Set(
      (data.primaryLocations || []).map((l: DoctorLocation) =>
        l.heading.toLowerCase(),
      ),
    );
    const privateHeadings = new Set(
      (data.privateLocations || []).map((l: DoctorLocation) =>
        l.heading.toLowerCase(),
      ),
    );
    return nonBioTabs.map((t) => {
      const desc = t.description;
      if (Array.isArray(desc)) {
        const tagged = desc.map((loc) => ({
          ...normalizeConsultantLocation(loc, data),
          text: primaryHeadings.has(loc.heading.toLowerCase())
            ? 'Primary'
            : privateHeadings.has(loc.heading.toLowerCase())
              ? 'Private'
              : undefined,
        }));
        return {
          title: t.heading,
          heading: t.heading,
          description: tagged,
          styles: t.styles,
        };
      }
      return {
        title: t.heading,
        heading: t.heading,
        description: desc,
        styles: t.styles,
      };
    });
  }, [nonBioTabs, data]);

  const handleSendMessage = () => {
    pushGtmEvent(GTM_EVENTS.CLICK_DOCTOR_SEND_MESSAGE);
    setHasBeenOpened(true);
    setEnquiryOpen(true);
  };

  const handleCall = () => {
    const phone = data.bookable
      ? data.assistantPhoneNumber || LIPS_MAIN_PHONE
      : LIPS_MAIN_PHONE;
    if (phone) {
      pushGtmEvent(GTM_EVENTS.CALL_DOCTOR);
      window.location.href = `tel:${phone.replace(/\s/g, '')}`;
    }
  };

  return (
    <div className="tablet:px-6 desktop:px-8 tablet:pt-12 mx-auto max-w-7xl px-4 pt-8">
      <div className="desktop:flex-row desktop:items-start desktop:gap-16 flex flex-col gap-8">
        <div className="desktop:flex-1 w-full min-w-0">
          <div className="tablet:flex-row tablet:items-start tablet:justify-between tablet:gap-4 mb-5 flex flex-col gap-1 text-left">
            <div className="flex items-start gap-4">
              <div className="tablet:gap-8 flex min-w-0 flex-1 justify-start gap-5 overflow-x-auto pb-2">
                {stats.map((stat) => {
                  const inner = (
                    <>
                      <div className="tablet:size-16 bg-surface-faint border-border-divider-light mb-2 flex size-14 items-center justify-center rounded-full border">
                        <R2Image
                          path={stat.image}
                          alt={stat.label}
                          width={28}
                          height={28}
                        />
                      </div>
                      <span className="tablet:text-lg text-text-strong block text-base font-bold">
                        {stat.label}
                      </span>
                      <div className="text-text-default flex items-center gap-1 text-xs font-normal">
                        {stat.sub}
                      </div>
                    </>
                  );
                  if (stat.doctify && data.doctifySlug) {
                    return (
                      <a
                        key={stat.label}
                        href={`https://www.doctify.com/uk/specialist/${data.doctifySlug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex shrink-0 cursor-pointer flex-col items-center text-center transition-opacity hover:opacity-80"
                      >
                        {inner}
                      </a>
                    );
                  }
                  return (
                    <div
                      key={stat.label}
                      className="flex shrink-0 flex-col items-center text-center"
                    >
                      {inner}
                    </div>
                  );
                })}
              </div>
              {isDoctor && data.gmcNumber && shouldShowTeddyBear(data) && (
                <div className="tablet:hidden mt-1 block shrink-0 gap-1">
                  <R2Image
                    path="teddybear.webp"
                    alt="Teddy bear"
                    width={80}
                    height={80}
                  />
                  {data.ageLabel ? (
                    <p className="mt-1 shrink-0 text-center text-xs font-semibold whitespace-pre-line">
                      {data.ageLabel}
                    </p>
                  ) : (
                    <p className="mt-1 shrink-0 text-center text-xs font-semibold">
                      All ages
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="tablet:gap-2 flex flex-col gap-1">
              {isDoctor && data.gmcNumber && (
                <p className="tablet:text-sm text-secondary-dark shrink-0 text-xs font-semibold">
                  {data?.gmcLabel ?? TEXT.gmcLabel}{' '}
                  <span className="text-default-body font-normal">
                    {data.gmcNumber}
                  </span>
                </p>
              )}
              {isDoctor && !data.gmcNumber && data.hcpcNumber && (
                <p className="tablet:text-sm text-secondary-dark shrink-0 text-xs font-semibold">
                  {TEXT.hcpcLabel}{' '}
                  <span className="text-default-body font-normal">
                    {data.hcpcNumber}
                  </span>
                </p>
              )}
              {isDoctor && data.gmcNumber && shouldShowTeddyBear(data) && (
                <div className="tablet:flex hidden w-full flex-col items-center">
                  <R2Image
                    path="teddybear.webp"
                    alt="Teddy bear"
                    width={80}
                    height={80}
                  />
                  {data.ageLabel ? (
                    <p className="mt-1 shrink-0 text-center text-xs font-semibold whitespace-pre-line">
                      {data.ageLabel}
                    </p>
                  ) : (
                    <p className="mt-1 shrink-0 text-center text-xs font-semibold">
                      All ages
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <h2 className="tablet:text-3xl desktop:text-4xl text-text-strong mb-4 text-2xl font-bold">
            {TEXT.about} {displayName}
          </h2>

          <SpecialistBiography text={biography} styles={bioTab?.styles} />

          {pillsTabs.length > 0 && (
            <div className="mt-8">
              <Suspense
                fallback={
                  <div
                    className="h-[200px] w-full animate-pulse rounded-xl bg-gray-50"
                    aria-hidden="true"
                  />
                }
              >
                <SpecialistTabs tabs={pillsTabs as any} doctor={data} />
              </Suspense>
            </div>
          )}
        </div>

        <aside className="desktop:w-90 desktop:sticky desktop:top-8 flex w-full shrink-0 flex-col gap-4">
          <Suspense
            fallback={
              <div
                className="h-[480px] w-full animate-pulse rounded-xl bg-gray-50"
                aria-hidden="true"
              />
            }
          >
            <BookingCard doctor={isDoctor ? data : null} />
          </Suspense>
          <div
            id="contact-section"
            className="tablet:p-5 rounded-xl border border-gray-200 bg-white p-4"
          >
            <p className="text-text-strong tablet:text-lg mb-3 text-base font-bold">
              {TEXT.contactTitle}
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleSendMessage}
                className="text-primary hover:border-theme-primary hover:text-theme-primary flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border border-gray-100 px-3 py-3 text-sm font-normal transition-all duration-200"
              >
                <R2Image
                  path="/images/specialists/mail.svg"
                  alt="Email"
                  width={15}
                  height={15}
                  className="transition-colors duration-200"
                />
                <span>{TEXT.messageButton}</span>
              </button>
              <button
                onClick={handleCall}
                className="text-primary hover:border-theme-primary hover:text-theme-primary flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border border-gray-100 px-3 py-3 text-sm font-normal transition-all duration-200"
              >
                <R2Image
                  path="/images/specialists/phone.svg"
                  alt="Phone"
                  width={15}
                  height={15}
                  className="transition-colors duration-200"
                />
                <span>{TEXT.callButton}</span>
              </button>
            </div>
          </div>
          {data.languages?.length > 0 && (
            <div className="tablet:p-6 rounded-2xl border border-gray-100 bg-white p-5">
              <h3 className="tablet:text-base text-text-slate-dark mb-1 text-sm font-bold">
                {TEXT.languagesTitle}
              </h3>
              <p className="text-sm font-medium text-gray-500">
                {data.languages
                  .map((l: string) =>
                    typeof l === 'string' ? l : (l as any).title,
                  )
                  .join(', ')}
              </p>
            </div>
          )}
        </aside>
      </div>

      {isDoctor && data.doctifySlug && (
        <div ref={doctifyRef} className="tablet:mt-14 mt-10">
          <Suspense
            fallback={
              <div
                className="h-[160px] w-full animate-pulse rounded-xl bg-gray-50"
                aria-hidden="true"
              />
            }
          >
            <ConsultantDoctifyRatingCarousel doctifySlug={data.doctifySlug} />
          </Suspense>
        </div>
      )}

      {hasBeenOpened && (
        <FormSubmissionModal
          open={enquiryOpen}
          onOpenChange={setEnquiryOpen}
          formId="doctorEnquiry"
          schema={doctorEnquirySchema}
          fields={doctorEnquiryFields}
          defaultValues={doctorEnquiryDefaultValues}
          title={`Send a message to ${displayName}`}
          description="Fill in your details and our team will contact you."
          submitLabel="Send message"
          columns={2}
          className="flex flex-col gap-6"
          metadata={{ doctorName: displayName }}
          recipientContext={{
            doctorSlug: data.slug,
            doctorApiId: data.apiId,
          }}
        />
      )}
    </div>
  );
}
